import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import MoneyInput from "@/components/ui/input-money";
import { useDynamicOptions } from "@/hooks/use-dynamic-options";
import { DataService } from "@/services/data";
import { FieldDataType } from "@/types/dynamic-detail";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { evaluateDateValidators } from "@/utils/dynamic-form";
import { formatPercentageFromRatio, formatPercentageInputFromRatio, parsePercentageInputToRatio } from "@/utils/format-time";


type InlineEditFieldProps = {
    field: FieldDataType;
    objectType: string;
    objectId: string | number;
    onSuccess?: () => void;
    /** Other fields in the same section — used for cross-field date validators. */
    siblingFields?: FieldDataType[];
};

/**
 * Inline edit component for a section field.
 * Shows the value in read mode + a pencil on hover.
 * On click, switches to the editor matching the field_type.
 */
export function InlineEditField({ field, objectType, objectId, onSuccess, siblingFields = [] }: InlineEditFieldProps) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<any>(field.raw_value ?? field.value ?? "");
    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup success timer on unmount
    useEffect(() => {
        return () => {
            if (successTimer.current) clearTimeout(successTimer.current);
        };
    }, []);

    // Load dynamic options for select/combobox fields
    const { dynamicOptions, loadingOptions } = useDynamicOptions(editing ? [field] : []);

    // Reset value when field data changes (after refetch)
    useEffect(() => {
        if (!editing) {
            setValue(field.raw_value ?? field.value ?? "");
        }
    }, [field.raw_value, field.value, editing]);

    // Focus input when entering edit mode
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing]);

    const handleCancel = useCallback(() => {
        setEditing(false);
        setValidationError(null);
        setValue(field.raw_value ?? field.value ?? "");
    }, [field.raw_value, field.value]);

    /**
     * Single source of truth for date-field validity used by both the disabled state
     * of the save button and `handleSave`. Returns an error message, or `null` when
     * the value is acceptable.
     *
     * Rules:
     * - Non-date fields are always considered valid here.
     * - Empty value: blocks only when the field is required (clearing an optional
     *   field is a valid intent).
     * - Otherwise the value must match `YYYY-MM-DD` and pass the cross-field date
     *   validators (delegated to the shared `evaluateDateValidators`).
     */
    const validateDateField = useCallback((): string | null => {
        if (field.field_type !== "date") return null;
        const isRequired = field.required === true || (field.validators?.some(v => v.type === "required") ?? false);
        const str = value === null || value === undefined ? "" : String(value);
        if (str === "") {
            return isRequired ? "Please enter a valid date." : null;
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return "Please enter a valid date.";
        return evaluateDateValidators(field.validators, str, v => {
            const sibling = siblingFields.find(f => f.field_name === v.value_from_field);
            return v.value ?? sibling?.raw_value ?? sibling?.value;
        });
    }, [field.field_type, field.required, field.validators, value, siblingFields]);

    const isDateIncomplete = validateDateField() !== null;

    const handleSave = useCallback(async () => {
        const dateError = validateDateField();
        if (dateError) {
            setValidationError(dateError);
            return;
        }
        setValidationError(null);
        setSubmitting(true);
        try {
            const response = await DataService.client
                .url(`/crm/objects/${objectType}/form-action/`)
                .post({
                    action_type: "__inline_update__",
                    id: objectId,
                    field_name: field.field_name,
                    value: value
                })
                .json<{ success: boolean; error?: string }>();

            if (response?.success) {
                setEditing(false);
                setShowSuccess(true);
                successTimer.current = setTimeout(() => setShowSuccess(false), 2000);
                onSuccess?.();
            } else {
                toast.error(response?.error || "Error while saving");
            }
        } catch (e: any) {
            try {
                const parsed = JSON.parse(e?.message);
                toast.error(parsed.error || "Error while saving");
            } catch {
                toast.error("Error while saving");
            }
        } finally {
            setSubmitting(false);
        }
    }, [objectType, objectId, field.field_name, value, validateDateField, onSuccess]);

    // Handle Enter key to save, Escape to cancel
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSave();
            } else if (e.key === "Escape") {
                handleCancel();
            }
        },
        [handleSave, handleCancel]
    );

    if (!editing) {
        return (
            <div className="flex min-w-0 items-center gap-1">
                <span className="min-w-0 flex-1 truncate text-sm" title={String(field.value ?? "")}>
                    {formatDisplayValue(field)}
                </span>
                {showSuccess ? (
                    <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                ) : (
                    <button
                        type="button"
                        className="flex-shrink-0"
                        onClick={() => setEditing(true)}
                        title="Edit"
                    >
                        <Pencil className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                    </button>
                )}
            </div>
        );
    }

    // Edit mode — render the appropriate editor
    return (
        <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex min-w-0 items-center gap-1">
                <div className="min-w-0 flex-1">
                    {renderEditor(
                        field,
                        value,
                        (v) => { setValue(v); setValidationError(null); },
                        handleKeyDown,
                        inputRef,
                        dynamicOptions,
                        loadingOptions
                    )}
                </div>
                {submitting ? (
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />
                ) : (
                    <>
                        <button type="button" onClick={handleCancel} title="Cancel" className="flex-shrink-0">
                            <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            title="Save"
                            disabled={isDateIncomplete}
                            className={`flex-shrink-0 ${isDateIncomplete ? "cursor-not-allowed opacity-30" : ""}`}
                        >
                            <Check className="h-4 w-4 text-gray-400 hover:text-green-500" />
                        </button>
                    </>
                )}
            </div>
            {validationError && (
                <p className="text-xs text-red-500">{validationError}</p>
            )}
        </div>
    );
}

/** Format a display value for read mode */
function formatDisplayValue(field: FieldDataType): string {
    const val = field.value;
    if (val == null || val === "") return "-";

    switch (field.field_type) {
        case "checkbox":
            return val ? "Yes" : "No";
        case "currency":
            return typeof val === "number" ? val.toLocaleString("fr-FR", { style: "currency", currency: "EUR" }) : String(val);
        case "datetime":
        case "datetime-local": {
            try {
                const dt = DateTime.fromISO(String(val));
                return dt.isValid ? dt.toFormat("dd/MM/yyyy HH:mm") : String(val);
            } catch {
                return String(val);
            }
        }
        case "date": {
            try {
                const dt = DateTime.fromISO(String(val).split("T")[0].split(" ")[0]);
                return dt.isValid ? dt.toFormat("dd/MM/yyyy") : String(val);
            } catch {
                return String(val);
            }
        }
        case "percentage":
            return formatPercentageFromRatio(Number(val));
        default:
            return String(val);
    }
}

/** Render the inline editor based on field_type */
function renderEditor(
    field: FieldDataType,
    value: any,
    setValue: (v: any) => void,
    handleKeyDown: (e: React.KeyboardEvent) => void,
    inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    dynamicOptions: Record<string, { value: string | null; label: string }[]>,
    loadingOptions: Record<string, boolean>
) {
    const type = field.field_type || "text";
    const options = field.options?.length ? field.options : dynamicOptions[field.field_name] || [];
    const formattedOptions = options.map((opt: any) => (typeof opt === "string" ? { value: opt, label: opt } : opt));
    const isLoading = loadingOptions[field.field_name];

    switch (type) {
        case "checkbox":
            return (
                <Checkbox
                    checked={!!value}
                    onCheckedChange={(checked) => setValue(!!checked)}
                    className="h-4 w-4"
                />
            );

        case "textarea":
            return (
                <Textarea
                    ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                    value={value ?? ""}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="min-h-[60px] text-sm"
                    rows={2}
                />
            );

        case "combobox":
            if (isLoading) return <span className="text-xs text-gray-400">Loading...</span>;
            return (
                <Combobox
                    name={field.field_name}
                    options={formattedOptions}
                    value={value ?? ""}
                    onValueChange={(v) => setValue(v || "")}
                    className="min-h-[2rem] text-sm"
                    placeholder="Select"
                    multiple={field.multiple}
                />
            );

        case "select":
            if (isLoading) return <span className="text-xs text-gray-400">Loading...</span>;
            return (
                <Select value={value?.toString() ?? ""} onValueChange={(v) => setValue(v)}>
                    <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                        {formattedOptions.map((opt: any) => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>
                                {opt.label || opt.value}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );

        case "datetime-local": {
            const dt = value ? DateTime.fromISO(String(value)) : DateTime.now();
            return (
                <DateTimePicker
                    date={dt.isValid ? dt : DateTime.now()}
                    setDate={(v) => setValue(v?.toISO({ suppressMilliseconds: true }) || "")}
                    className="w-full"
                />
            );
        }

        case "date":
            return (
                <Input
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type="date"
                    value={value ?? ""}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                />
            );

        case "currency":
            return (
                <MoneyInput
                    value={value ?? 0}
                    onValueChange={(v) => setValue(v ?? 0)}
                />
            );

        case "number":
            return (
                <Input
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type="number"
                    value={value ?? ""}
                    onChange={(e) => setValue(e.target.value === "" ? "" : Number(e.target.value))}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                />
            );

        case "percentage":
            return (
                <Input
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type="number"
                    step="0.01"
                    value={value == null || value === "" ? "" : formatPercentageInputFromRatio(Number(value))}
                    onChange={(e) => setValue(parsePercentageInputToRatio(e.target.value) ?? "")}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                />
            );

        default:
            // text, email, tel, url, etc.
            return (
                <Input
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type={type === "duration" ? "text" : type}
                    value={value ?? ""}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                    maxLength={field.max_length}
                />
            );
    }
}

